
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface CIAtivo {
  id: string;
  ci_id: string;
  nome: string;
  tipo_nome: string | null;
  status_operacional: string | null;
}

export interface CILicenca {
  id: string;
  ci_id: string;
  software_nome: string;
  chave_licenca: string | null;
  status: string | null;
}

export const useSolicitacaoCIs = (solicitacaoId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ['solicitacao-cis', solicitacaoId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data: cis, error } = await supabase
        .from('solicitacao_cis')
        .select('id, ci_tipo, ci_id')
        .eq('solicitacao_id', solicitacaoId);

      if (error) throw error;
      if (!cis || cis.length === 0) return { ativos: [] as CIAtivo[], licencas: [] as CILicenca[] };

      const ativoIds = cis.filter(c => c.ci_tipo === 'ativo').map(c => c.ci_id);
      const licencaIds = cis.filter(c => c.ci_tipo === 'licenca').map(c => c.ci_id);

      let ativos: CIAtivo[] = [];
      let licencas: CILicenca[] = [];

      if (ativoIds.length > 0) {
        const { data: ativosData } = await supabase
          .from('cmdb_ativos')
          .select('id, nome, tipo_id, tipo_nome, status_operacional')
          .in('id', ativoIds);

        // Buscar nomes dos tipos
        const tipoIds = [...new Set((ativosData || []).map(a => a.tipo_id).filter(Boolean))];
        let tiposMap: Record<string, string> = {};
        if (tipoIds.length > 0) {
          const { data: tiposData } = await supabase
            .from('cmdb_tipos_ativos')
            .select('id, nome')
            .in('id', tipoIds);
          tiposMap = Object.fromEntries((tiposData || []).map(t => [t.id, t.nome]));
        }

        ativos = (ativosData || []).map((a: any) => {
          const ci = cis.find(c => c.ci_id === a.id && c.ci_tipo === 'ativo')!;
          const tipoLabel = (a.tipo_id && tiposMap[a.tipo_id]) || a.tipo_nome || '-';
          return { id: ci.id, ci_id: a.id, nome: a.nome, tipo_nome: tipoLabel, status_operacional: a.status_operacional };
        });
      }

      if (licencaIds.length > 0) {
        const { data: licencasData } = await supabase
          .from('cmdb_software_licencas')
          .select('id, chave_licenca, status, software_id, cmdb_softwares(nome)')
          .in('id', licencaIds);

        licencas = (licencasData || []).map((l: any) => {
          const ci = cis.find(c => c.ci_id === l.id && c.ci_tipo === 'licenca')!;
          return {
            id: ci.id,
            ci_id: l.id,
            software_nome: l.cmdb_softwares?.nome || 'N/A',
            chave_licenca: l.chave_licenca,
            status: l.status,
          };
        });
      }

      return { ativos, licencas };
    },
    enabled: !!solicitacaoId,
  });

  const addCI = useMutation({
    mutationFn: async ({ ciTipo, ciId }: { ciTipo: 'ativo' | 'licenca'; ciId: string }) => {
      const { error } = await supabase.from('solicitacao_cis').insert({
        solicitacao_id: solicitacaoId,
        ci_tipo: ciTipo,
        ci_id: ciId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: 'CI vinculado com sucesso' });
    },
    onError: () => {
      toast({ title: 'Erro ao vincular CI', variant: 'destructive' });
    },
  });

  const removeCI = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('solicitacao_cis').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: 'CI removido com sucesso' });
    },
    onError: () => {
      toast({ title: 'Erro ao remover CI', variant: 'destructive' });
    },
  });

  return {
    ativos: data?.ativos || [],
    licencas: data?.licencas || [],
    isLoading,
    addCI,
    removeCI,
  };
};
